/**
 * SubtitleTemplateSelector - 字幕模板选择器
 *
 * 功能: 在 VideoInputPage 中提供字幕模板选择
 * 特点: 仅展示模板选择,不包含自定义样式配置
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Type } from 'lucide-react';
import type { SubtitleTemplate, SubtitleStyle } from '../types';
import LivePreview from './subtitle/LivePreview';

interface SubtitleTemplateSelectorProps {
  /** 字幕模板列表 */
  templates: SubtitleTemplate[];
  /** 当前字幕样式 */
  subtitleStyle: SubtitleStyle;
  /** 字幕样式变更回调 */
  onSubtitleStyleChange: (style: SubtitleStyle) => void;
}

const SubtitleTemplateSelector: React.FC<SubtitleTemplateSelectorProps> = ({
  templates,
  subtitleStyle,
  onSubtitleStyleChange,
}) => {
  const { t } = useTranslation();

  // 获取当前选中的模板名称
  const selectedTemplateName = templates.find(t => t.id === subtitleStyle.templateId)?.name
    || t('inputBar.subtitle.default');

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-4 mb-4 transition-all duration-300 opacity-100">
      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-white flex items-center gap-2">
          <Type className="w-4 h-4" />
          {t('inputBar.subtitle.title')}
        </h3>
        {/* 当前选中的模板 */}
        <span className="text-xs text-gray-400">
          {selectedTemplateName}
        </span>
      </div>

      {/* 模板选择网格 - 自适应宽度,3行+滚动 */}
      <div className="bg-gray-800/95 backdrop-blur-sm border border-gray-700/50 rounded-lg p-3 max-h-64 overflow-y-auto custom-scrollbar">
        <div className="grid grid-cols-3 gap-2">
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => onSubtitleStyleChange({ ...template.style, templateId: template.id })}
              className={`
                p-2 rounded-lg border transition-all duration-200
                ${subtitleStyle.templateId === template.id
                  ? 'border-purple-500/50 bg-purple-500/10 shadow-sm'
                  : 'border-gray-700/40 hover:border-gray-600/60 hover:bg-gray-700/30'
                }
              `}
            >
              <div className="text-center">
                {/* 模板名称 */}
                <div className="text-xs font-medium text-white mb-1 truncate">
                  {template.name}
                </div>
                {/* 迷你预览 - 自适应宽度 */}
                <LivePreview
                  style={template.style}
                  text={t('subtitle.templatePreview')}
                  width={120}
                  height={72}
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubtitleTemplateSelector;
